/**
 * Patient Facade
 *
 * Stable API surface for patient operations.
 * Aggregates actions and workflows for presentation layer consumption.
 * Handles Patient and related resources (Coverage, RelatedPerson, CareTeam, ServiceRequest, Task).
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type { Patient, Reference } from "@medplum/fhirtypes";
import type { UpdatePatientInput } from "@benchmark/core/actions";
import {
  createPatientWithRelatedResources,
  updatePatientWithRelatedResources,
  pausePatient as pausePatientWorkflow,
  unpausePatient as unpausePatientWorkflow,
  type PausePatientInput,
} from "@benchmark/core/workflows";
import type { PatientFormData } from "@benchmark/core/types";

/**
 * Creates a Patient facade instance bound to a MedplumClient.
 * @agentic connections
 */
export function createPatientFacade(medplum: MedplumClient) {
  return {
    /**
     * Create a new patient with related resources atomically via transaction bundle.
     * @agentic connections
     */
    create: (
      formData: PatientFormData,
      authorReference?: Reference,
    ): Promise<Patient> => {
      return createPatientWithRelatedResources(
        medplum,
        formData,
        authorReference,
      );
    },

    /**
     * Update an existing patient with related resources atomically. Coverage handled separately.
     * @agentic connections
     */
    update: (input: UpdatePatientInput): Promise<Patient> => {
      return updatePatientWithRelatedResources(medplum, input);
    },

    /**
     * Pause a patient's care plan. Returns flag, care plan, and tasks with adjusted status.
     * @agentic connections
     */
    pause: (input: PausePatientInput) => {
      return pausePatientWorkflow(medplum, input);
    },

    /**
     * Unpause a patient's care plan. Returns resumed care plan and recalculated task status.
     * @agentic connections
     */
    unpause: (patient: Patient) => {
      return unpausePatientWorkflow(medplum, patient);
    },
  };
}
